import React, { useState, useEffect, useRef } from 'react';
import { useBackupPolling } from '../../hooks/useBackupPolling';
import { Clock } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './OwnerNotifications.css';

const OwnerNotifications = ({ onBack, businessId, onConfirmOrder, onCancelOrder }) => {
  const [notifications, setNotifications] = useState([]);
  const [removingIds, setRemovingIds] = useState(new Set());
  const [toast, setToast] = useState(null);
  const previousNotificationCount = useRef(0);

  // Voice announcement function
  const announceNewOrder = (notification) => {
    // Default to enabled if not set
    const voiceEnabled = localStorage.getItem('ownerVoiceEnabled') !== 'false';
    
    
    if (!voiceEnabled) {
      return;
    }
    
    if (!window.speechSynthesis) {
      return;
    }

    const tableNum = notification.tableNumber === 'TAKEAWAY' 
      ? 'takeaway' 
      : `table ${notification.tableNumber}`;
    
    let announcement = `New order from ${tableNum}. `;
    
    if (notification.items && notification.items.length > 0) {
      notification.items.forEach(item => {
        const itemName = item.name || item.menu_item_name || 'item';
        const quantity = item.quantity || 1;
        announcement += `${quantity} ${itemName}. `;
      });
    }

    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(announcement);
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-IN';
    
    utterance.onerror = (e) => console.error('❌ Voice error:', e);
    
    const voices = window.speechSynthesis.getVoices();
    
    if (voices.length > 0) {
      const indianVoice = voices.find(voice => 
        voice.lang.includes('en-IN') || voice.lang.includes('en-GB') || voice.lang.includes('en-US')
      );
      if (indianVoice) {
        utterance.voice = indianVoice;
      }
    }
    
    window.speechSynthesis.speak(utterance);
  };

  // Load pending orders from backend
  useEffect(() => {
    if (!businessId) return;
    loadPendingOrders();
    
    // The backup poll behind this WebSocket listener now lives in the
    // useBackupPolling call below, so it goes quiet while the counter tablet
    // sits untouched. The socket is what actually delivers new orders.

    // WebSocket listener for real-time notifications
    const handleNewOrderNotification = (data) => {
      
      if (data.businessId === businessId) {
        
        // Play voice announcement immediately
        const notification = {
          tableNumber: data.tableNumber,
          orderNumber: data.orderNumber,
          items: data.items
        };
        announceNewOrder(notification);
        
        // Reload orders to show the new one
        loadPendingOrders();
      }
    };
    
    // Listen for WebSocket events
    if (window.socket) {
      window.socket.on('owner:new_order_notification', handleNewOrderNotification);
    } else {
      console.warn('⚠️ [WEBSOCKET] Socket not available');
    }
    
    return () => {
      if (window.socket) {
        window.socket.off('owner:new_order_notification', handleNewOrderNotification);
      }
    };
  }, [businessId]);

  // Safety net for a dropped socket. This used to run every 2s regardless of
  // whether anyone was there — on a tablet left on overnight that alone kept the
  // Neon compute from ever suspending, since Neon only sleeps after 5 minutes of
  // silence. It now runs only while the screen is actually being used.
  useBackupPolling(() => loadPendingOrders(), 15000, { enabled: Boolean(businessId) });

  const loadPendingOrders = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      const url = `${API_BASE}/qr-billing/owner-pending-orders?businessId=${businessId}`;
      
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });


      if (response.ok) {
        const data = await response.json();
        const newOrders = data.orders || [];
        
        // Check if there are new orders (count increased)
        if (newOrders.length > previousNotificationCount.current && previousNotificationCount.current > 0) {
          // Find the new orders (ones not in previous state)
          const newOrderIds = new Set(notifications.map(n => n.id));
          const freshOrders = newOrders.filter(order => !newOrderIds.has(order.id));
          
          
          // Announce each new order
          freshOrders.forEach(order => {
            announceNewOrder(order);
          });
        }
        
        previousNotificationCount.current = newOrders.length;
        setNotifications(newOrders);
      } else {
        console.error(`❌ [FRONTEND] Failed to fetch: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading pending orders:', error);
    }
  };

  const handleConfirm = async (notification) => {
    
    // Add to removing set for animation
    setRemovingIds(prev => new Set([...prev, notification.id]));
    
    try {
      // Remove from local state IMMEDIATELY (optimistic update)
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      
      // Call parent confirm handler
      if (onConfirmOrder) {
        await onConfirmOrder(notification.id);
      }
      
      // Clean up removing set
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
      
      // Don't reload immediately - let the automatic polling (every 2s) handle it
      // This prevents race conditions with stale data
      
      // Show success toast
      setToast({ type: 'success', message: '✅ Order marked as ready!' });
      setTimeout(() => setToast(null), 3000);
      
    } catch (error) {
      console.error('❌ Error confirming order:', error);
      
      // On error, reload to restore correct state
      await loadPendingOrders();
      
      // Remove from removing set on error
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
      
      // Show error toast
      setToast({ type: 'error', message: '❌ Failed to confirm order: ' + error.message });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleCancel = async (notification) => {
    // Add to removing set for animation
    setRemovingIds(prev => new Set([...prev, notification.id]));
    
    // Wait for animation to complete (300ms)
    setTimeout(async () => {
      try {
        // Call parent cancel handler
        if (onCancelOrder) {
          await onCancelOrder(notification.id);
        }
        
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setRemovingIds(prev => {
          const next = new Set(prev);
          next.delete(notification.id);
          return next;
        });
        
        // Show cancelled toast
        setToast({ type: 'error', message: '❌ Order cancelled' });
        setTimeout(() => setToast(null), 3000);
        
      } catch (error) {
        console.error('❌ Error cancelling order:', error);
        // Remove from removing set on error
        setRemovingIds(prev => {
          const next = new Set(prev);
          next.delete(notification.id);
          return next;
        });
      }
    }, 300);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="owner-notifications-container">
      {toast && (
        <div className={`owner-toast owner-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
      <div className="owner-notifications-header">
        <button 
          className="owner-notifications-back-btn"
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" />
        </button>
        <h1 className="owner-notifications-title">Owner Notifications</h1>
      </div>

      <div className="owner-notifications-content">
        {notifications.length === 0 ? (
          <div className="notification-empty-state">
            <div className="notification-empty-icon">🔔</div>
            <h3>No Pending Orders</h3>
          </div>
        ) : (
          notifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`notification-card ${removingIds.has(notification.id) ? 'notification-card-removing' : ''}`}
            >
              <div className="notification-card-header">
                <div className="notification-order-info">
                  <h2 className="notification-order-number">
                    Order {notification.order_number || notification.orderNumber || notification.id.substring(0, 8)}
                  </h2>
                  <p className="notification-table-number">
                    {notification.tableNumber === 'TAKEAWAY' ? ' Take Away' : `Table ${notification.tableNumber}`}
                  </p>
                </div>
                {notification.payment_method && notification.payment_method !== 'pending' && (
                  <span className="notification-upi-badge">
                    {notification.payment_method === 'counter' 
                      ? 'PAID AT COUNTER'
                      : notification.payment_method.toUpperCase()}
                  </span>
                )}
              </div>

              <div className="notification-items-section">
                <p className="notification-items-title">Items ({notification.items?.length || 0})</p>
                {notification.items?.map((item, index) => {
                  // Parse customizations if it's a string
                  let customizations = item.customizations;
                  if (typeof customizations === 'string') {
                    try {
                      customizations = JSON.parse(customizations);
                    } catch (e) {
                      customizations = [];
                    }
                  }
                  
                  return (
                    <div key={index} className="notification-item">
                      <div className="notification-item-content">
                        <span className="notification-item-name">
                          {item.name} x{item.quantity}
                        </span>
                        {customizations && Array.isArray(customizations) && customizations.length > 0 && (
                          <div className="notification-item-customizations">
                            {customizations.map((custom, idx) => (
                              <span key={idx} className="customization-tag">
                                {custom.optionName || custom.name || custom}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="notification-item-price">₹{item.price || item.unit_price || 0}</span>
                    </div>
                  );
                })}
              </div>

              <div className="notification-footer">
                <div className="notification-timestamp">
                  <Clock size={16} />
                  <span>{formatTimestamp(notification.timestamp || notification.created_at)}</span>
                </div>
                <div className="notification-total">₹{notification.total || 0}</div>
              </div>

              <div className="notification-actions">
                <button 
                  className="notification-btn notification-btn-cancel"
                  onClick={() => handleCancel(notification)}
                  disabled={removingIds.has(notification.id)}
                >
                  Cancel
                </button>
                <button 
                  className="notification-btn notification-btn-confirm"
                  onClick={() => handleConfirm(notification)}
                  disabled={removingIds.has(notification.id)}
                >
                  {notification.status === 'awaiting_owner_notification' 
                    ? 'Mark as Ready' 
                    : 'Approve & Send'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OwnerNotifications;
